import React, { useEffect, useState } from 'react';
import useIntl from '@afosto/utils-react/intl/useIntl';
import usePermissions from '@afosto/utils-react/permissions/usePermissions';
import useSubscriptions from '@afosto/utils-react/subscription/useSubscriptions';
import { useAppActions } from '@afosto/app-components-react/AppProvider';
import { useSnackbar } from 'notistack';
import { FormattedMessage } from 'react-intl';
import Avatar from '@material-ui/core/Avatar';
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CardActions from '@material-ui/core/CardActions';
import CardContent from '@material-ui/core/CardContent';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Icon from '@afosto/app-components-react/Icon';
import SkeletonBody from '@afosto/components-react/SkeletonBody';
import appData from '@afosto/utils-react/subscription/apps';
import sortArrayByProperty from '@afosto/utils/array/sortArrayByProperty';
import { subscription as subscriptionApi } from '@afosto/iam/api/Subscription';
import translations from './translations';
import styled from 'styled-components';

const AppIconContainer = styled.div`
  padding-left: ${props => props.theme.spacing()}px;
  padding-top: ${props => props.theme.spacing(2)}px;
`;

const AppIcon = styled(Avatar)`
  && {
    background: none;
    color: #999;
  }
`;

const AppCard = styled(Card)`
  && {
    display: flex;
    height: 100%;
  }
`;

const AppActions = styled(CardActions)`
  && {
    align-items: flex-start;
    padding-top: ${props => props.theme.spacing(2)}px;
  }
`;

const AppContent = styled(CardContent)`
  && {
    flex: 1;
  }
`;

const AppStatus = styled(Typography)`
  && {
    color: ${props => props.theme.palette.success.main};
    font-weight: 500;
    line-height: normal;
    padding-right: ${props => props.theme.spacing(0.5)}px;
    padding-top: ${props => props.theme.spacing(0.5)}px;
  }
`;

const ApplicationOverview = () => {
  const [hasPermissions] = usePermissions(['iam:subscriptions:read']);
  const intl = useIntl();
  const [apps, setApps] = useState([]);
  const [isFetchingApps, setIsFetchingApps] = useState(true);
  const { enqueueSnackbar } = useSnackbar();
  const subscriptions = useSubscriptions();
  const [actions] = useAppActions();

  useEffect(() => {
    const fetchApps = async () => {
      try {
        setIsFetchingApps(true);
        const response = await subscriptionApi.listProducts();
        const licensedApps = response.data.filter(app => app.plan.type === 'license');
        const formattedApps = licensedApps.reduce((acc, app) => {
          const availableApp = appData.find(({ key: appKey }) => appKey === app.code);
          const isSubscribed = subscriptions.some(({ code: appCode }) => appCode === app.code);

          if (!availableApp) {
            return acc;
          }

          return [
            ...acc,
            {
              ...app,
              icon: availableApp.icon || '',
              isDefault: app.code === 'iam',
              isSubscribed,
            },
          ];
        }, []);

        const defaultApps = formattedApps.filter(app => app.isDefault);
        const nonDefaultApps = formattedApps.filter(app => !app.isDefault);

        setApps([
          ...sortArrayByProperty(defaultApps, 'name'),
          ...sortArrayByProperty(nonDefaultApps, 'name'),
        ]);
      } catch (err) {
        // TODO: Show user that apps could not be loaded.
      } finally {
        setIsFetchingApps(false);
      }
    };

    if (subscriptions.length > 0) {
      fetchApps();
    } else {
      setApps([]);
    }
  }, [subscriptions]);

  const handleAddSubscription = ({ code }) => async () => {
    try {
      await subscriptionApi.addSubscription({ code });

      const appIndex = apps.findIndex(app => app.code === code);
      const currentApp = apps.find(app => app.code === code);
      const otherApps = apps.filter(app => app.code !== code);
      const newApps = [...otherApps];

      newApps.splice(appIndex, 0, { ...currentApp, isSubscribed: true });
      setApps([...newApps]);

      actions.setShouldRefreshSubscriptions(true);
      enqueueSnackbar(intl.formatMessage(translations.page.feedback.subscriptionAdded), {
        variant: 'success',
      });
    } catch (err) {
      enqueueSnackbar(intl.formatMessage(translations.page.feedback.subscriptionNotAdded), {
        variant: 'error',
      });
    }
  };

  if (isFetchingApps) {
    return (
      <Grid container spacing={1}>
        {Array.from(Array(10).keys()).map(app => (
          <Grid item xs={12} md={6} key={app}>
            <AppCard>
              <AppIconContainer>
                <AppIcon>
                  <Icon
                    icon={['fal', 'home']}
                    style={{
                      color: 'transparent',
                      backgroundColor: '#f3f3f3',
                      borderRadius: '50%',
                    }}
                  />
                </AppIcon>
              </AppIconContainer>
              <AppContent>
                <Typography variant="subtitle2" gutterBottom component="div">
                  <SkeletonBody lines={1} style={{ marginBottom: '0.3rem' }} />
                </Typography>
                <Typography variant="body2" component="div">
                  <SkeletonBody lines={2} verticalGutter="0.4rem" style={{ height: '0.4rem' }} />
                </Typography>
              </AppContent>
              <AppActions>
                <SkeletonBody lines={1} style={{ height: '1.25rem', width: '4rem' }} />
              </AppActions>
            </AppCard>
          </Grid>
        ))}
      </Grid>
    );
  }

  return (
    <Grid container spacing={1}>
      {apps.map(app => (
        <Grid item xs={12} md={6} key={app.code}>
          <AppCard>
            <AppIconContainer>
              <AppIcon>
                <Icon icon={['fal', app.icon]} />
              </AppIcon>
            </AppIconContainer>
            <AppContent>
              <Typography variant="subtitle2">
                <FormattedMessage
                  id={`pages.contract.apps.${app.code}.title`}
                  defaultMessage={app.name}
                  description={`The name of the app for code ${app.code}`}
                />
              </Typography>
              <Typography variant="body2">
                <FormattedMessage
                  id={`pages.contract.apps.${app.code}.description`}
                  defaultMessage={app.description}
                  description={`The description of the app for code ${app.code}`}
                />
              </Typography>
            </AppContent>
            <AppActions>
              {!app.isSubscribed ? (
                hasPermissions() && (
                  <Button
                    variant="outlined"
                    size="small"
                    color="primary"
                    onClick={handleAddSubscription(app)}
                  >
                    <FormattedMessage {...translations.subscribe} />
                  </Button>
                )
              ) : (
                <AppStatus variant="overline">
                  <FormattedMessage {...translations.active} />
                </AppStatus>
              )}
            </AppActions>
          </AppCard>
        </Grid>
      ))}
    </Grid>
  );
};

export default ApplicationOverview;
